import React from 'react';
import PropTypes from 'prop-types';
import { Table } from 'antd';
import { isEmpty, map, toLower } from 'lodash';
import {
  MultiHeader,
  SingleHeader,
  SingleDataColumn,
  MultiDataColumn
} from '../Components';
import { ResultTableWrapper } from './styled';
import { normalizeAlertScore } from '../../../../util/normalization';

import { convertISOToDateFormat } from '../../../../util/timezone';
import { TextComponent } from '../../components/TableComponents';
import {
  TM_FRAUD_PREVENTION_SOURCE_MAPPING,
  TM_FRAUD_SOURCE_IDS
} from '../../../../util/GlobalConstant';

const ResultTable = ({
  isLoading,
  results,
  paginationProps,
  onPaginationChange,
  totalResults,
  onAlertIdClick,
  hasAccessToAlertDetails
}) => {
  const columns = [
    {
      title: (
        <MultiHeader header="Alert ID" subHeaders={['Alert Generation Date']} />
      ),
      width: '15%',
      ellipsis: true,
      render: (_, record) => {
        const {
          alertId = '-',
          alertDate = '-',
          reviewStatus = 'Not Started'
        } = record;

        if (hasAccessToAlertDetails) {
          return (
            <MultiDataColumn.Link
              pop
              data={[
                {
                  text: alertId,
                  to: `/app/trm/run-instance/${alertId}`,
                  ...(toLower(reviewStatus) === 'not started'
                    ? {
                        onClick: () => {
                          onAlertIdClick(alertId);
                        }
                      }
                    : {})
                },
                {
                  text: convertISOToDateFormat(alertDate) || '-'
                }
              ]}
            />
          );
        } else {
          return (
            <MultiDataColumn
              data={[
                { text: alertId },
                {
                  text: convertISOToDateFormat(alertDate) || '-'
                }
              ]}
              pop
            />
          );
        }
      }
    },

    {
      title: <MultiHeader header="Alert Score" />,
      width: '8%',
      render: (_, record) => {
        const { alertLevel = '-', alertScore = 0 } = record;
        const value = normalizeAlertScore(Number(alertScore).toFixed(2));
        return (
          <SingleDataColumn.ProgressBar value={value} level={alertLevel} />
        );
      }
    },
    {
      title: <MultiHeader header="Party ID" subHeaders={['Party Name']} />,
      width: '15%',
      ellipsis: true,
      render: (_, record) => {
        const { primaryPartyKey = '-', primaryPartyName = '-' } = record;
        return (
          <MultiDataColumn
            data={[{ text: primaryPartyKey }, { text: primaryPartyName }]}
            pop
          />
        );
      }
    },
    {
      title: <MultiHeader header="Account NO" subHeaders={['Account Type']} />,
      width: '10%',
      ellipsis: true,
      render: (_, record) => {
        const { accountId = '-', accountType = '-' } = record;
        return (
          <MultiDataColumn
            pop
            data={[{ text: accountId }, { text: accountType }]}
          />
        );
      }
    },

    {
      title: (
        <MultiHeader header="Typology Name" subHeaders={['Typology ID']} />
      ),
      width: '10%',
      ellipsis: true,
      render: (_, record) => {
        const { triggeredTypologies = [] } = record;
        const typologyNames = map(triggeredTypologies, 'name').join(', ');
        const typologyIds = map(triggeredTypologies, 'id').join(', ');
        return (
          <MultiDataColumn
            pop
            data={[
              { text: isEmpty(typologyNames) ? '-' : typologyNames },
              { text: isEmpty(typologyIds) ? '-' : typologyIds }
            ]}
          />
        );
      }
    },
    {
      title: (
        <MultiHeader
          header="Run Instance Name"
          subHeaders={['Run Instance ID', 'Run Instance Execution ID']}
        />
      ),
      width: '13%',
      ellipsis: true,
      render: (_, record) => {
        const {
          runInstanceName = '-',
          runInstanceId = '-',
          runInstanceExecutionId = '-'
        } = record;
        return (
          <MultiDataColumn
            pop
            data={[
              { text: runInstanceName },
              { text: runInstanceId },
              { text: runInstanceExecutionId }
            ]}
          />
        );
      }
    },
    {
      title: <SingleHeader header="FinCrime Type" />,
      width: '9%',
      render: (_, record) => {
        const { alertSourceId = '' } = record;
        return (
          <TextComponent
            id={
              alertSourceId === TM_FRAUD_SOURCE_IDS.TM
                ? TM_FRAUD_PREVENTION_SOURCE_MAPPING.TM_AML
                : alertSourceId === TM_FRAUD_SOURCE_IDS.FRAUD_PREVENTION_ID
                ? TM_FRAUD_PREVENTION_SOURCE_MAPPING.FRAUD_PREVENTION
                : TM_FRAUD_PREVENTION_SOURCE_MAPPING.FRAUD_DETECTION
            }
          />
        );
      }
    },
    {
      title: <SingleHeader header="Review Status" />,
      width: '9%',
      render: (_, record) => {
        const { reviewStatus = 'Not Started' } = record;
        return <SingleDataColumn.Status type={reviewStatus} />;
      }
    },
    {
      title: <SingleHeader header="Review Feedback" />,
      width: '10%',
      render: (_, record) => {
        const { alertQuality = '-' } = record;
        return <SingleDataColumn.Feedback type={alertQuality} />;
      }
    }
  ];

  return (
    <ResultTableWrapper>
      <Table
        loading={isLoading}
        columns={columns}
        dataSource={results}
        rowKey="alertId"
        pagination={{
          total: totalResults,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: onPaginationChange,
          showTotal: (total) => `${total} Records`,
          ...paginationProps
        }}
      />
    </ResultTableWrapper>
  );
};

ResultTable.propTypes = {
  results: PropTypes.array,
  isLoading: PropTypes.bool,
  totalResults: PropTypes.number,
  paginationProps: PropTypes.object,
  onPaginationChange: PropTypes.func,
  onAlertIdClick: PropTypes.func
};

export default ResultTable;
